export const LL_TARGETS = [4, 6, 8, 12, 14, 16, 24, 28, 30, 32, 48, 56, 60, 62, 64];

export const MIN_TEAMS = 3;
export const MAX_TEAMS = 64;

const isPow2 = (x) => (x & (x - 1)) === 0;

export const nextPow2 = (x) => {
  let b = 1;
  while (b < x) b *= 2;
  return b;
};

const canonicalPositions = (B) => {
  let a = [1, 2];
  while (a.length < B) {
    const size = a.length * 2;
    const next = [];
    for (const s of a) {
      next.push(s, size + 1 - s);
    }
    a = next;
  }
  return a;
};

const classicLabel = (size) => ({ kind: 'classic', size });
const numberedLabel = (n) => ({ kind: 'numbered', n });

const pairNext = (next) => {
  const paired = [];
  for (let i = 0; i < next.length; i += 2) paired.push([next[i], next[i + 1]]);
  return paired;
};

const playRound = (entrants, midRef) => {
  const matches = [];
  const next = [];
  for (const [a, b] of entrants) {
    if (a.t === 'bye' || b.t === 'bye') {
      const real = a.t === 'bye' ? b : a;
      matches.push({ bye: true, adv: real });
      next.push(real);
    } else {
      midRef.n += 1;
      const id = 'P' + midRef.n;
      matches.push({ id, a, b });
      next.push({ t: 'winner', of: id });
    }
  }
  return { matches, next };
};

export const buildByes = (n) => {
  const B = nextPow2(n);
  const pos = canonicalPositions(B);
  const rounds = [];
  let entrants = [];
  for (let i = 0; i < B; i += 2) {
    const a = pos[i];
    const b = pos[i + 1];
    entrants.push([
      a <= n ? { t: 'team', s: a } : { t: 'bye' },
      b <= n ? { t: 'team', s: b } : { t: 'bye' },
    ]);
  }
  const midRef = { n: 0 };
  for (;;) {
    const count = entrants.length * 2;
    const { matches, next } = playRound(entrants, midRef);
    rounds.push({ label: classicLabel(count), matches });
    if (next.length === 1) break;
    entrants = pairNext(next);
  }
  return { rounds, target: B, byes: B - n, repechageAfter: -1 };
};

export const buildLL = (n) => {
  const T = LL_TARGETS.find((t) => t >= n);
  if (isPow2(T)) {
    return { ...buildByes(n), resolvedToByes: true };
  }
  let m = T;
  while (m % 2 === 0) m /= 2;
  const byes = T - n;
  const rounds = [];
  const midRef = { n: 0 };
  let entrants = [];
  let teamIdx = 1;
  for (let i = 0; i < T / 2; i++) {
    if (i < byes) {
      entrants.push([{ t: 'team', s: teamIdx++ }, { t: 'bye' }]);
    } else {
      entrants.push([{ t: 'team', s: teamIdx++ }, { t: 'team', s: teamIdx++ }]);
    }
  }
  let rn = 0;
  for (;;) {
    rn += 1;
    const { matches, next } = playRound(entrants, midRef);
    rounds.push({ label: numberedLabel(rn), matches });
    if (next.length === m) break;
    entrants = pairNext(next);
  }
  const repechageAfter = rounds.length - 1;
  const F = m + 1;
  const pos = canonicalPositions(F);
  const seeds = [];
  for (let i = 1; i <= m; i++) seeds.push({ t: 'wseed', s: i });
  seeds.push({ t: 'll' });
  let ent = [];
  for (let i = 0; i < F; i += 2) {
    ent.push([seeds[pos[i] - 1], seeds[pos[i + 1] - 1]]);
  }
  for (;;) {
    const count = ent.length * 2;
    const { matches, next } = playRound(ent, midRef);
    rounds.push({ label: classicLabel(count), matches });
    if (next.length === 1) break;
    ent = pairNext(next);
  }
  return { rounds, target: T, byes, repechageAfter, oddPart: m };
};

export const buildStructure = (mode, n) =>
  mode === 'll' ? buildLL(n) : buildByes(n);

const feederIds = (match, struct) => {
  const ids = [];
  const prelim = struct.repechageAfter >= 0 ? struct.rounds[struct.repechageAfter] : null;
  const prelimReal = prelim ? prelim.matches.filter((m) => !m.bye) : [];
  for (const e of [match.a, match.b]) {
    if (!e) continue;
    if (e.t === 'winner') ids.push(e.of);
    if (e.t === 'wseed' && prelimReal[e.s - 1]) ids.push(prelimReal[e.s - 1].id);
    if (e.t === 'll') prelimReal.forEach((m) => ids.push(m.id));
  }
  return ids;
};

export const scheduleMatches = (struct, opts) => {
  const { courts, dur, gap, teamRest, third, ceremony, ceremonyMin } = opts;
  const sim = courts.map((c) => ({ ...c, next: c.start, slots: [] }));
  const placed = [];
  const unplaced = [];
  const endOf = {};
  let barrier = -1;
  const waves = struct.rounds.map((r, ri) => ({
    label: r.label,
    ri,
    real: r.matches.filter((x) => !x.bye).map((m) => ({ m, feeders: feederIds(m, struct) })),
  }));
  if (third && waves.length > 1) {
    const fi = waves.length - 1;
    const semiIds = waves[fi - 1].real.map((x) => x.m.id);
    waves.splice(fi, 0, {
      label: { kind: 'third' },
      ri: waves[fi].ri,
      real: [{ m: { id: '3P', third: true }, feeders: semiIds }],
    });
  }
  for (const wave of waves) {
    let waveEnd = barrier;
    for (const { m: match, feeders } of wave.real) {
      let ready = barrier < 0 ? 0 : barrier;
      for (const f of feeders) {
        if (endOf[f] !== undefined && endOf[f] + teamRest > ready) {
          ready = endOf[f] + teamRest;
        }
      }
      let best = null;
      sim.forEach((c, ci) => {
        const st = Math.max(c.next, ready, c.start);
        if (st + dur <= c.end && (best === null || st < best.st)) {
          best = { ci, st };
        }
      });
      if (best === null) {
        unplaced.push({ match, wave });
        continue;
      }
      const en = best.st + dur;
      endOf[match.id] = en;
      sim[best.ci].slots.push({ match, wave, st: best.st, en });
      sim[best.ci].next = en + gap;
      placed.push({ match, wave, st: best.st, en, court: best.ci });
      if (en > waveEnd) waveEnd = en;
    }
    if (waveEnd > barrier) barrier = waveEnd + gap;
  }
  let cer = null;
  if (ceremony && placed.length) {
    const lastEnd = Math.max(...placed.map((p) => p.en));
    const finalCourt = placed.reduce((a, p) => (p.en >= a.en ? p : a)).court;
    cer = { st: lastEnd, en: lastEnd + ceremonyMin, court: finalCourt };
    sim[finalCourt].slots.push({ ceremony: true, st: cer.st, en: cer.en });
  }
  sim.forEach((c) => c.slots.sort((a, b) => a.st - b.st));
  const lastEnd = placed.length ? Math.max(...placed.map((p) => p.en)) : null;
  return { courts: sim, placed, unplaced, cer, endAll: cer ? cer.en : lastEnd };
};

export const teamLetter = (s) =>
  s <= 26
    ? String.fromCharCode(64 + s)
    : String.fromCharCode(64 + Math.floor((s - 1) / 26)) + String.fromCharCode(65 + ((s - 1) % 26));

export const fmtTime = (m) => {
  const h = Math.floor(m / 60) % 24;
  const mm = m % 60;
  return h + ':' + String(mm).padStart(2, '0');
};

export const fmtTimeInput = (m) => {
  const h = Math.floor(m / 60) % 24;
  const mm = m % 60;
  return String(h).padStart(2, '0') + ':' + String(mm).padStart(2, '0');
};

export const parseTimeInput = (v) => {
  const [h, m] = v.split(':').map(Number);
  return h * 60 + m;
};
